import { entries } from "@impero-com/common/utils/entries";
import { assertNever } from "@impero-com/common/utils/obj";
import moment from "moment-timezone";

import {
    DatabaseRecurringOccurrenceForDatabaseOccurrenceTwoMonthsAndDatabaseDueDateOffsetForMonthDatabaseOffsetPeriod as EveryTwoMonthsDatabaseRecurringOccurrence,
    DatabaseRecurringOccurrenceForDatabaseOccurrenceHalfYearAndDatabaseDueDateOffsetForMonthDatabaseOffsetPeriod as HalfYearlyDatabaseRecurringOccurrence,
    DatabaseRecurringOccurrenceForDatabaseOccurrenceMonthAndDatabaseDueDateOffsetForMonthDatabaseOffsetPeriod as MonthlyDatabaseRecurringOccurrence,
    DatabaseRecurringOccurrenceForDatabaseOccurrenceQuarterAndDatabaseDueDateOffsetForMonthDatabaseOffsetPeriod as QuarterlyDatabaseRecurringOccurrence,
    DatabaseDueDateOffsetForMonthDatabaseOffsetPeriod as MonthDatabaseDueDateOffset,
    InitialFieldType,
    SubsequentFieldType,
    ControlStepGroup,
    NotificationType,
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    HalfYear,
    Month,
    Quarter,
    TwoMonths,
    DataGridColumn,
    SubsequentAssignmentKind,
    NotificationRecipientKind,
    DatabaseRecurrenceRule,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    ReducedLegacyAssignee,
    ReducedLegacyReviewer,
    LegacyAllocatedTime,
    LegacyNotificationRecipient,
    LegacyReviewerOffsets,
    ReducedLegacyControl,
    LegacyCondition,
    LegacyDataGridColumn,
    LegacyTask,
    LegacyTaskKind,
    ReducedLegacyUser,
    ReducedLegacyUserGroup,
} from "api/generated/model";
import { Field, FieldCondition } from "components/fieldBuilder/models";
import { Guid } from "utils/guid";

import {
    ControlViewFormState,
    ParsedControlScheduling,
    toParsedControlScheduling,
} from "../formData";

type DatabaseRecurrenceTime = Pick<
    DatabaseRecurrenceRule,
    "Hour" | "Minute" | "Timezone"
>;
type DatabaseRecurrenceStartDate = Pick<
    Extract<DatabaseRecurrenceRule, { $type: "WeeklyRecurrence" }>,
    "StartYear" | "StartMonth" | "StartDay"
>;

const LEGACY_SELF_ORG_ID = 42;
const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm:ss.SSSSSSSSS";

export interface LegacyControlView {
    Control: ReducedLegacyControl;
    IsNewControl: boolean;
    IsSendImmediately: boolean;
}

const toLegacyRecurrenceBase = (
    scheduling: ParsedControlScheduling
): DatabaseRecurrenceTime => {
    const dueTime = moment(scheduling.dueTime, TIME_FORMAT);
    return {
        Hour: dueTime.hour(),
        Minute: dueTime.minute(),
        Timezone: {
            Name: scheduling.timezoneName,
        },
    };
};
const toLegacyRecurrenceBaseWithStartDate = (
    scheduling: ParsedControlScheduling
): DatabaseRecurrenceTime & DatabaseRecurrenceStartDate => {
    const startDate = moment(scheduling.startDate, DATE_FORMAT);
    return {
        ...toLegacyRecurrenceBase(scheduling),
        StartYear: startDate.year(),
        StartMonth: startDate.month() + 1,
        StartDay: startDate.date(),
    };
};

const legacyMonthMap: Record<string, number> = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
};
const legacy2MonthMap: Record<TwoMonths, number> = {
    JanuaryFebruary: 1,
    MarchApril: 2,
    MayJune: 3,
    JulyAugust: 4,
    SeptemberOctober: 5,
    NovemberDecember: 6,
};

const legacyQuarterMap: Record<Quarter, number> = {
    First: 1,
    Second: 2,
    Third: 3,
    Fourth: 4,
};

const legacyHalfYearMap: Record<HalfYear, number> = {
    First: 1,
    Second: 2,
};

const toLegacyMonthDueDateOffset = (
    defaultDueDateOffset: MonthDueDateOffset
): MonthDatabaseDueDateOffset => ({
    Anchor: defaultDueDateOffset.anchor,
    OffsetType: defaultDueDateOffset.offsetType,
    OffsetPeriod: {
        Days: defaultDueDateOffset.offsetPeriod.days,
        Months: defaultDueDateOffset.offsetPeriod.months,
    },
});

const toLegacyMonths = (
    months: Partial<Record<Month, DefaultOrCustomOffset>>
): MonthlyDatabaseRecurringOccurrence[] => {
    const legacyMonths = entries(months).map(([month, value]) => {
        return {
            Value: legacyMonthMap[month],
            DueDateOffset:
                value !== "Default"
                    ? toLegacyMonthDueDateOffset(value.Custom)
                    : null,
        };
    });
    return legacyMonths;
};

const toLegacy2Months = (
    months: Partial<Record<TwoMonths, DefaultOrCustomOffset>>
): EveryTwoMonthsDatabaseRecurringOccurrence[] => {
    const legacy2Months = entries(months).map(([month, value]) => {
        return {
            Value: legacy2MonthMap[month],
            DueDateOffset:
                value !== "Default"
                    ? toLegacyMonthDueDateOffset(value.Custom)
                    : null,
        };
    });
    return legacy2Months;
};

const toLegacyQuarters = (
    quarters: Partial<Record<Quarter, DefaultOrCustomOffset>>
): QuarterlyDatabaseRecurringOccurrence[] => {
    const legacyQuarters = entries(quarters).map(([quarter, value]) => {
        return {
            Value: legacyQuarterMap[quarter],
            DueDateOffset:
                value !== "Default"
                    ? toLegacyMonthDueDateOffset(value.Custom)
                    : null,
        };
    });
    return legacyQuarters;
};

const toLegacyHalfYears = (
    halfYears: Partial<Record<HalfYear, DefaultOrCustomOffset>>
): HalfYearlyDatabaseRecurringOccurrence[] => {
    const legacyQuarters = entries(halfYears).map(([halfYear, value]) => {
        return {
            Value: legacyHalfYearMap[halfYear],
            DueDateOffset:
                value !== "Default"
                    ? toLegacyMonthDueDateOffset(value.Custom)
                    : null,
        };
    });
    return legacyQuarters;
};

export const toLegacyRecurrenceAndOptions = (
    scheduling: ParsedControlScheduling
): {
    recurrence: DatabaseRecurrenceRule;
    calendarId: number | null;
    financialYearId: number | null;
} => {
    let recurrence: DatabaseRecurrenceRule;
    let calendarId: number | null;
    let financialYearId: number | null;
    switch (scheduling.rule.type) {
        case "Date": {
            recurrence = {
                $type: "DateRecurrence",
                Year: moment(scheduling.startDate, DATE_FORMAT).year(),
                Month: moment(scheduling.startDate, DATE_FORMAT).month() + 1,
                Day: moment(scheduling.startDate, DATE_FORMAT).date(),
                ...toLegacyRecurrenceBase(scheduling),
            };
            calendarId = null;
            financialYearId = null;
            break;
        }
        case "Weekly": {
            recurrence = {
                $type: "WeeklyRecurrence",
                Periodicity: scheduling.rule.periodicity,
                DaysOfWeek: scheduling.rule.days,
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = null;
            financialYearId = null;
            break;
        }
        case "Monthly": {
            recurrence = {
                $type: "MonthlyRecurrence",
                Months: toLegacyMonths(scheduling.rule.occurrences),
                DefaultDueDateOffset: toLegacyMonthDueDateOffset(
                    scheduling.rule.defaultDueDateOffset
                ),
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = scheduling.rule.calendarId ?? null;
            financialYearId = null;
            break;
        }
        case "EveryTwoMonths": {
            recurrence = {
                $type: "Every2MonthsRecurrence",
                TwoMonths: toLegacy2Months(scheduling.rule.occurrences),
                DefaultDueDateOffset: toLegacyMonthDueDateOffset(
                    scheduling.rule.defaultDueDateOffset
                ),
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = scheduling.rule.calendarId ?? null;
            financialYearId = null;
            break;
        }
        case "Quarterly": {
            recurrence = {
                $type: "QuarterlyRecurrence",
                Quarters: toLegacyQuarters(scheduling.rule.occurrences),
                DefaultDueDateOffset: toLegacyMonthDueDateOffset(
                    scheduling.rule.defaultDueDateOffset
                ),
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = scheduling.rule.calendarId ?? null;
            financialYearId = scheduling.rule.financialYearId ?? null;
            break;
        }
        case "HalfYearly": {
            recurrence = {
                $type: "HalfYearlyRecurrence",
                HalfYears: toLegacyHalfYears(scheduling.rule.occurrences),
                DefaultDueDateOffset: toLegacyMonthDueDateOffset(
                    scheduling.rule.defaultDueDateOffset
                ),
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = scheduling.rule.calendarId ?? null;
            financialYearId = scheduling.rule.financialYearId ?? null;
            break;
        }
        case "Yearly": {
            recurrence = {
                $type: "YearlyRecurrence",
                Periodicity: scheduling.rule.periodicity,
                DefaultDueDateOffset: toLegacyMonthDueDateOffset(
                    scheduling.rule.defaultDueDateOffset
                ),
                ...toLegacyRecurrenceBaseWithStartDate(scheduling),
            };
            calendarId = scheduling.rule.calendarId ?? null;
            financialYearId = scheduling.rule.financialYearId ?? null;
            break;
        }
        default:
            assertNever(scheduling.rule);
    }
    return {
        recurrence,
        calendarId,
        financialYearId,
    };
};

const blankUserData: Omit<ReducedLegacyUser, "Id"> = {
    Firstname: "",
    Lastname: "",
    Email: "",
    Culture: "en",
    Timezone: {
        Name: "",
    },
};

const blankUserGroupData: Omit<ReducedLegacyUserGroup, "Id"> = {
    Name: "",
    Description: null,
};

const toLegacyReviewers = (
    stepGroup: ControlStepGroup,
    groupId: Guid
): Pick<
    ReducedLegacyAssignee,
    "Id" | "TagIds" | "FirstReviewer" | "Reviewer"
> => {
    const reviewerStep = stepGroup.subsequentSteps[0];
    const secondReviewerStep = stepGroup.subsequentSteps[1];

    const toLegacyReviewer = (
        assignment: SubsequentAssignmentKind
    ): ReducedLegacyReviewer => {
        switch (assignment.type) {
            case "User": {
                return {
                    $type: "UserReviewer",
                    Id: groupId,
                    User: {
                        ...blankUserData,
                        Id: assignment.userId,
                    },
                };
            }
            case "Pool": {
                return {
                    $type: "UserPoolReviewer",
                    Id: groupId,
                    UserPool: {
                        ...blankUserGroupData,
                        Id: assignment.userGroupId,
                    },
                };
            }
            default:
                assertNever(assignment);
        }
    };

    const firstReviewerAssignment =
        secondReviewerStep.assignment[groupId] !== undefined &&
        reviewerStep.assignment[groupId];
    const secondReviewerAssignment =
        secondReviewerStep.assignment[groupId] ??
        reviewerStep.assignment[groupId];
    return {
        Id: groupId,
        TagIds: [],
        FirstReviewer: firstReviewerAssignment
            ? toLegacyReviewer(firstReviewerAssignment)
            : null,
        Reviewer: secondReviewerAssignment
            ? toLegacyReviewer(secondReviewerAssignment)
            : null,
    };
};

const toLegacyAssignees = (
    stepGroup: ControlStepGroup
): ReducedLegacyAssignee[] => {
    const responsibleAssignments = stepGroup.initialStep.assignment;
    const legacyAssignees: ReducedLegacyAssignee[] = Object.entries(
        responsibleAssignments
    ).map(([id, assignment]) => {
        let assignee: ReducedLegacyAssignee;
        switch (assignment.type) {
            case "User":
                assignee = {
                    $type: "UserAssignee",
                    User: {
                        ...blankUserData,
                        Id: assignment.userId,
                    },
                    EntityId: null,
                    ...toLegacyReviewers(stepGroup, id),
                };
                break;
            case "Pool":
                assignee = {
                    $type: "UserPoolAssignee",
                    UserPool: {
                        ...blankUserGroupData,
                        Id: assignment.userGroupId,
                    },
                    DefaultClaimantUserId: assignment.defaultClaimantId,
                    EntityId: null,
                    ...toLegacyReviewers(stepGroup, id),
                };
                break;
            case "Group":
                assignee = {
                    $type: "UserGroupAssignee",
                    UserGroup: {
                        ...blankUserGroupData,
                        Id: assignment.userGroupId,
                    },
                    EntityId: null,
                    ...toLegacyReviewers(stepGroup, id),
                };
                break;
            default:
                assertNever(assignment);
        }
        return assignee;
    });

    return legacyAssignees;
};

const toLegacyReviewerOffsets = (
    stepGroup: ControlStepGroup
): LegacyReviewerOffsets => {
    const toLegacyAllocatedTime = (step: 0 | 1): LegacyAllocatedTime => {
        const allocatedTime = stepGroup.subsequentSteps[step].allocatedTime;
        return {
            Days: allocatedTime.days,
            Microseconds: allocatedTime.microseconds,
        };
    };
    return {
        FirstReviewerAllocatedTime: toLegacyAllocatedTime(0),
        SecondReviewerAllocatedTime: toLegacyAllocatedTime(1),
    };
};

const toLegacyDataGridColumn = (
    column: DataGridColumn
): LegacyDataGridColumn => {
    const { id, title, type } = column;
    return {
        Id: id,
        Title: title,
        Type: type,
    };
};

const toLegacyCondition = (
    condition: FieldCondition<string>
): LegacyCondition => {
    const { action, operator, fieldId, value } = condition;
    return {
        Action: action,
        Operator: {
            $type: operator,
            LHS: { Value: fieldId },
            RHS: { Value: value },
        },
    };
};

// Woot, we can handle any field type
const anyFieldToLegacyTask = (
    field: Field<InitialFieldType, string> | Field<SubsequentFieldType, string>
): LegacyTask => {
    const {
        id,
        title,
        instructions,
        fieldType,
        mandatory,
        condition,
        blobIds,
    } = field;
    let legacyType: LegacyTaskKind;
    switch (fieldType.type) {
        case "TextInput": {
            legacyType = {
                $type: "SingleLineText",
            };
            break;
        }
        case "TextArea": {
            legacyType = {
                $type: "Text",
            };
            break;
        }
        case "NumericInput": {
            legacyType = {
                $type: "Numeric",
            };
            break;
        }
        case "MoneyInput": {
            legacyType = {
                $type: "Money",
                DefaultCurrency: fieldType.defaultCurrency,
            };
            break;
        }
        case "RadioButtonGroup": {
            legacyType = {
                $type: "RadioButtons",
                Options: fieldType.options,
            };
            break;
        }
        case "CheckboxGroup": {
            legacyType = {
                $type: "Checkbox",
                Options: fieldType.options,
            };
            break;
        }
        case "Dropdown": {
            legacyType = {
                $type: "Dropdown",
                Options: fieldType.options,
            };
            break;
        }
        case "FileUpload": {
            legacyType = {
                $type: "File",
            };
            break;
        }
        case "Heading": {
            legacyType = {
                $type: "Heading",
            };
            break;
        }
        case "DataGrid": {
            legacyType = {
                $type: "DataGrid",
                Columns: fieldType.columns.map(toLegacyDataGridColumn),
                InitialNumberOfRows: fieldType.initialNumberOfRows,
            };
            break;
        }
        case "DataSheet": {
            legacyType = {
                $type: "DataSheet",
                DataSheetId: fieldType.dataSheetId,
                Grouping: fieldType.grouping,
            };
            break;
        }
        default: {
            return assertNever(fieldType);
        }
    }
    return {
        Id: id,
        Title: title,
        Instructions: instructions,
        Type: legacyType,
        Mandatory: mandatory,
        BlobNames: blobIds.map((blobId) => ({ BlobName: blobId })),
        Condition: condition ? toLegacyCondition(condition) : null,
    };
};

function toLegacyNotificationRecipients(
    notificationRecipients: Record<
        string,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >
): Record<
    string,
    Partial<Record<NotificationType, LegacyNotificationRecipient[]>>
> {
    const legacyNotificationRecipients: Record<
        string,
        Partial<Record<NotificationType, LegacyNotificationRecipient[]>>
    > = {};
    for (const [assignmentGroupId, recipientsByType] of entries(
        notificationRecipients
    )) {
        const legacyRecipientsByType: Partial<
            Record<NotificationType, LegacyNotificationRecipient[]>
        > = {};
        legacyNotificationRecipients[assignmentGroupId] =
            legacyRecipientsByType;
        for (const [notificationType, recipients] of entries(
            recipientsByType
        )) {
            const legacyRecipients: LegacyNotificationRecipient[] =
                recipients.map((recipient) => {
                    switch (recipient.type) {
                        case "User": {
                            return {
                                $type: "UserNotificationRecipient",
                                User: {
                                    ...blankUserData,
                                    Id: recipient.userId,
                                },
                            };
                        }
                        case "Group": {
                            return {
                                $type: "GroupNotificationRecipient",
                                UserGroup: {
                                    ...blankUserGroupData,
                                    Id: recipient.userGroupId,
                                },
                            };
                        }
                        default: {
                            return assertNever(recipient);
                        }
                    }
                });
            legacyRecipientsByType[notificationType] = legacyRecipients;
        }
    }
    return legacyNotificationRecipients;
}

export const toReducedLegacyControl = (
    controlView: ControlViewFormState
): ReducedLegacyControl => {
    // This is assumed to be validated by the schema
    // Would be fixed by having something like ValidatedControlViewFormState, ideally inferred from the schema, but it is currently incomplete
    if (controlView.controlProgramId === null) {
        throw new Error("Expected control.controlProgramId to be non-null");
    }
    const parsedControlScheduling = controlView.scheduling
        ? toParsedControlScheduling(controlView.scheduling)
        : null;
    const { recurrence, calendarId, financialYearId } = parsedControlScheduling
        ? toLegacyRecurrenceAndOptions(parsedControlScheduling)
        : { recurrence: null, calendarId: null, financialYearId: null };
    return {
        Id: controlView.id,
        Title: controlView.title,
        Description: controlView.description ?? "",
        TagIds: controlView.tagIds,
        RecurrenceRule: recurrence,
        CalendarId: calendarId,
        FinancialYearId: financialYearId,
        ControlProgramId: controlView.controlProgramId,
        Assignees: toLegacyAssignees(controlView.stepGroup),
        OrganizationId: LEGACY_SELF_ORG_ID,
        SegregationOfDutiesEnabled:
            controlView.assignmentSettings.segregationOfDutiesEnabled,
        ReviewerOffsets: toLegacyReviewerOffsets(controlView.stepGroup),
        NotificationOffset: parsedControlScheduling
            ? {
                  DaysBefore:
                      controlView.stepGroup.initialStep.allocatedTime.days,
                  Hour: parsedControlScheduling.sendTime.hour(),
                  Minute: parsedControlScheduling.sendTime.minute(),
              }
            : null,
        LoginProtectionEnabled:
            controlView.assignmentSettings.loginProtectionEnabled,
        CoveredPeriod: controlView.scheduling?.coveredPeriod
            ? {
                  Unit: controlView.scheduling.coveredPeriod.type,
                  Offset: controlView.scheduling.coveredPeriod.data,
              }
            : null,
        FieldsByStep: [
            controlView.stepGroup.initialStep.fieldGroup.fields.map(
                anyFieldToLegacyTask
            ),
            controlView.stepGroup.subsequentSteps[0].fieldGroup.fields.map(
                anyFieldToLegacyTask
            ),
            controlView.stepGroup.subsequentSteps[1].fieldGroup.fields.map(
                anyFieldToLegacyTask
            ),
        ],
        LastUpdated: null,
        LastUpdatedBy: null,
        NotificationRecipients: toLegacyNotificationRecipients(
            controlView.stepGroup.notificationRecipients
        ),
    };
};

export const toReducedLegacyControlForConfirmationData = (
    controlView: ControlViewFormState,
    isNewControl: boolean,
    isSendImmediately: boolean
): LegacyControlView => {
    return {
        Control: toReducedLegacyControl(controlView),
        IsNewControl: isNewControl,
        IsSendImmediately: isSendImmediately,
    };
};
